import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionCardMenu,
  useObjectActions,
  type ObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import type { InboxItem } from "@cohesive/api-client";
import { ChatCard } from "@cohesive/ui-components";
import { useMemo } from "react";

export interface ChatCardContainerProps {
  item: InboxItem;
  isSelected: boolean;
  onOpen: (item: InboxItem) => void;
  onDelete: (item: InboxItem) => void;
  onRename?: (item: InboxItem) => void;
  onToggleStar?: (item: InboxItem) => void;
  /** Selection/activation click (cmd/shift multi-select). When omitted, a plain
   *  click opens the chat directly (e.g. Home recents with no selection). */
  onClick?: (e: React.MouseEvent) => void;
}

const CHAT_KIND: ObjectKind = { type: "chat" };

/**
 * Registry-backed `ChatCard`. Owns one `useRailRowContextMenu` controller so the
 * card's kebab and right-click open the same manifest-driven menu.
 */
export function ChatCardContainer({
  item,
  isSelected,
  onOpen,
  onDelete,
  onRename,
  onToggleStar,
  onClick,
}: ChatCardContainerProps) {
  const controller = useRailRowContextMenu();

  const handlers = useMemo<ObjectActionHandlers>(
    () => ({
      open: () => onOpen(item),
      rename: onRename ? () => onRename(item) : undefined,
      delete: () => onDelete(item),
      star: onToggleStar ? () => onToggleStar(item) : undefined,
      unstar: onToggleStar ? () => onToggleStar(item) : undefined,
    }),
    [item, onOpen, onRename, onDelete, onToggleStar],
  );

  const { actions } = useObjectActions({
    kind: CHAT_KIND,
    surface: "context-menu",
    enabled: controller.open,
    state: { isFavorite: !!item.is_starred },
    handlers,
  });

  return (
    <ChatCard
      item={item}
      isSelected={isSelected}
      onClick={onClick ?? (() => onOpen(item))}
      onDoubleClick={onClick ? () => onOpen(item) : undefined}
      onContextMenu={controller.onContextMenu}
      onToggleStar={onToggleStar}
      menuSlot={
        <ObjectActionCardMenu
          controller={controller}
          actions={actions}
          kind={CHAT_KIND}
          name={item.flow_session?.name || "Untitled chat"}
        />
      }
    />
  );
}
